import { Link, useLocation } from "react-router-dom";
import { HashLink } from "react-router-hash-link";
import { useState, useEffect } from "react";
import { motion, useScroll, AnimatePresence } from "framer-motion";
import { fetchFeatureFlags } from "../config/featureFlags";
import { fetchEvent } from "../data/event";
import { formatEventDate } from "../utils/formatEventDate";

export default function Navbar() {
  const [isOpen, setIsOpen] = useState(false);
  const [showDateBar, setShowDateBar] = useState(false);
  const [showBandsLink, setShowBandsLink] = useState(false); // <- flag
  const { scrollY } = useScroll();
  const location = useLocation();

  const toggleMenu = () => setIsOpen(!isOpen);

  const scrollWithOffset = (el: HTMLElement) => {
    const yOffset = -80;
    const y = el.getBoundingClientRect().top + window.pageYOffset + yOffset;
    window.scrollTo({ top: y, behavior: "smooth" });
  };

  useEffect(() => {
    fetchFeatureFlags().then(flags => setShowBandsLink(flags.showBandsSection));
  }, []);

  const [event, setEvent] = useState<null | string>(null);
  useEffect(() => {
    fetchEvent().then((data) =>
      setEvent(formatEventDate(data.date))
    );
  }, []);

  useEffect(() => {
    if (location.pathname !== "/") {
      setShowDateBar(false);
      return;
    }
    const unsub = scrollY.on("change", (y) => {
      setShowDateBar(y > window.innerHeight * 0.9);
    });
    return () => unsub();
  }, [scrollY, location.pathname]);

  const handleRetrospectiveClick = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <>
      <nav className="fixed top-0 left-0 w-full bg-bleuClair text-white z-50 font-bebas">
        <div className="container mx-auto flex justify-between items-center p-4">
          <HashLink
            smooth
            to="/#top"
            className="text-xl tracking-wide hover:text-orange transition-colors duration-500"
            onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
          >
            Festival de Musique d'Ensemble
          </HashLink>

          {/* Menu desktop */}
          <div className="hidden md:flex space-x-6 text-xl uppercase">
            <HashLink smooth to="/#info" scroll={scrollWithOffset} className="hover:text-orange transition-colors duration-500">
              Infos
            </HashLink>

            {showBandsLink && (
              <HashLink smooth to="/#bands" scroll={scrollWithOffset} className="hover:text-orange transition-colors duration-500">
                Groupes
              </HashLink>
            )}

            <HashLink smooth to="/#sponsors" scroll={scrollWithOffset} className="hover:text-orange transition-colors duration-500">
              Partenaires
            </HashLink>
            <HashLink smooth to="/#contact" scroll={scrollWithOffset} className="hover:text-orange transition-colors duration-500">
              Contact
            </HashLink>
            <Link
              to="/retrospective"
              onClick={handleRetrospectiveClick}
              className={`hover:text-orange transition-colors duration-500 ${location.pathname === "/retrospective" ? "text-orange" : ""
                }`}
            >
              Rétrospective
            </Link>
            <a
              href="https://www.lagaronne.fr"
              target="_blank"
              rel="noopener noreferrer"
              className="text-white hover:text-orange transition-colors duration-500"
            >
              La Garonne
            </a>
          </div>

          {/* Bouton menu mobile */}
          <button
            className="md:hidden flex items-center focus:outline-none"
            onClick={toggleMenu}
          >
            <svg
              className="w-6 h-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              {isOpen ? (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              ) : (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              )}
            </svg>
          </button>
        </div>

        {/* Menu mobile */}
        <AnimatePresence>
          {isOpen && (
            <motion.div
              key="mobileMenu"
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.5, ease: "easeInOut" }}
              className="md:hidden bg-bleuClair overflow-hidden"
            >
              <div className="flex flex-col items-center space-y-4 py-4 uppercase text-xl">

                <>

                  {(() => {
                    const closeDelay = 500;
                    const handleNavAndClose = (extra?: () => void) => (e?: React.MouseEvent) => {
                      if (extra) extra();
                      // laisse le temps au HashLink d'effectuer le scroll smooth
                      setTimeout(() => setIsOpen(false), closeDelay);
                    };
                    return (
                      <>
                        <HashLink
                          smooth
                          to="/#info"
                          scroll={scrollWithOffset}
                          className="hover:text-orange"
                          onClick={handleNavAndClose()}
                        >
                          Infos
                        </HashLink>

                        {showBandsLink && (
                          <HashLink
                            smooth
                            to="/#bands"
                            scroll={scrollWithOffset}
                            className="hover:text-orange"
                            onClick={handleNavAndClose()}
                          >
                            Groupes
                          </HashLink>
                        )}

                        <HashLink
                          smooth
                          to="/#sponsors"
                          scroll={scrollWithOffset}
                          className="hover:text-orange"
                          onClick={handleNavAndClose()}
                        >
                          Partenaires
                        </HashLink>

                        <HashLink
                          smooth
                          to="/#contact"
                          scroll={scrollWithOffset}
                          className="hover:text-orange"
                          onClick={handleNavAndClose()}
                        >
                          Contact
                        </HashLink>

                        <Link
                          to="/retrospective"
                          className="hover:text-orange"
                          onClick={() => {
                            // exécute l'action spécifique puis ferme après un bref délai
                            handleRetrospectiveClick();
                            setTimeout(() => setIsOpen(false), 200);
                          }}
                        >
                          Rétrospective
                        </Link>

                        <a
                          href="https://www.lagaronne.fr"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-white hover:text-orange"
                          onClick={() => setIsOpen(false)}
                        >
                          La Garonne
                        </a>
                      </>
                    );
                  })()}
                </>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </nav>

      {/* Bandeau fixe de date */}
      {location.pathname === "/" && (
        <motion.div
          initial={{ y: -50, opacity: 0 }}
          animate={showDateBar ? { y: 0, opacity: 1 } : { y: -50, opacity: 0 }}
          transition={{ duration: 0.5 }}
          className="fixed top-[60px] left-0 w-full bg-bleuFonce text-white text-center text-2xl py-2 z-40 font-titan"
        >
          {event}
        </motion.div>
      )}
    </>
  );
}